import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import type { JobPosition, JobPosting, TeamMember, CompanyTeam, AiChatMessage, AiChatRequest, AiConversationState, AiAutoFillSuggestions } from '@/types';
import { useAiChatStore, isSessionExpired, EMPTY_JOB_POSITION_SESSION } from '@/stores/aiChatStore';
import { useBillingStatus } from '@/hooks/useBillingStatus';
import { useUpgradeWallStore } from '@/stores/upgradeWallStore';
import { nextTierForCap } from '@/components/dashboard/CapUsageWidget';
import { useGlobalLoading } from '@/stores/loadingStore';
import { DEFAULT_LIST_PAGE_SIZE } from '@/components/shared/ListPagination';
import { useSkipInitialEffect } from '@/hooks/usePersistedListView';
import {
  isListViewLandingAfterReload,
  listViewStorageKey,
  loadSessionJson,
  type JobPositionsListPersist,
} from '@/lib/listViewPersistence';
import { emitBillingUsageChanged } from '@/lib/billingEvents';
import { setPositionsCache } from '@/lib/recruiterCache';
import { queryKeys } from '@/lib/queryKeys';
import {
  useCompanyTeamsQuery,
  usePostingsAllQuery,
  usePositionsQuery,
} from '@/hooks/useRecruiterListQueries';
import { normalizeJobLocation } from '@/lib/utils';

const POSITIONS_PAGE_SIZE = DEFAULT_LIST_PAGE_SIZE;

const emptyPosition: Partial<JobPosition> = {
  title: '', description: '', status: 'draft', seniority: undefined, contractType: undefined,
  workMode: undefined, location: '', team: '', salaryMin: undefined, salaryMax: undefined, salaryCurrency: 'EUR',
  requirePhone: false, requireLinkedIn: false, notifyCandidate: false, allowDuplicateApplications: false, assignedMembers: [],
  pipelineTemplateId: import.meta.env.VITE_DEFAULT_PIPELINE_TEMPLATE_ID || undefined,
  initialPipelineKey: import.meta.env.VITE_DEFAULT_INITIAL_PIPELINE_KEY || undefined,
};

/**
 * buildFormContextDelta — calculates the delta of form fields since the last AI turn.
 *
 * Only fields that are NEW (not in knownFields) and have a non-empty value are included.
 * This implements the delta tracking pattern (ADR Section 9.1):
 * - First turn: all non-empty fields are sent
 * - Subsequent turns: only NEW or CHANGED fields are sent
 */
type FormContextDelta = NonNullable<NonNullable<AiChatRequest['context']>['formContext']>;

function buildFormContextDelta(
  editData: Partial<JobPosition>,
  knownFields: string[]
): FormContextDelta {
  const delta: Record<string, unknown> = {};
  const trackableFields = [
    'title', 'description', 'seniority', 'contractType',
    'workMode', 'location', 'salaryMin', 'salaryMax', 'salaryCurrency'
  ];
  for (const field of trackableFields) {
    const value = editData[field as keyof JobPosition];
    if (value !== undefined && value !== null && value !== '' && !knownFields.includes(field)) {
      delta[field] = value;
    }
  }
  return delta as FormContextDelta;
}

export interface UseJobPositionsReturn {
  positions: JobPosition[];
  loading: boolean;
  setPositions: React.Dispatch<React.SetStateAction<JobPosition[]>>;
  postingsCountMap: Record<string, number>;
  filtered: JobPosition[];
  paginatedFiltered: JobPosition[];
  page: number;
  totalPages: number;
  totalFiltered: number;
  setPage: (page: number) => void;
  statusFilter: string;
  setStatusFilter: (v: string) => void;
  teamFilter: string;
  setTeamFilter: (v: string) => void;
  search: string;
  setSearch: (v: string) => void;
  dialogOpen: boolean;
  setDialogOpen: (v: boolean) => void;
  editData: Partial<JobPosition>;
  setEditData: React.Dispatch<React.SetStateAction<Partial<JobPosition>>>;
  deleteId: string | null;
  setDeleteId: (v: string | null) => void;
  saving: boolean;
  handleSave: () => Promise<void>;
  handleDelete: (id: string) => Promise<void>;
  handlePermanentDelete: (id: string) => Promise<void>;
  handleStatusToggle: (pos: JobPosition) => Promise<void>;
  openNewPosition: () => void;
  // Sprint 3 (S3.E) — post-create wizard
  wizardStep: 'form' | 'created';
  createdPosition: { id: string; title: string; landingPageUrl: string } | null;
  activatingNow: boolean;
  handleSaveAsDraft: () => void;
  handleActivateAndOpen: () => Promise<void>;
  copyCreatedPositionLink: () => Promise<void>;
  closeWizardAndReset: () => void;
  // AI Chat
  aiChatOpen: boolean;
  setAiChatOpen: (v: boolean) => void;
  aiMessages: AiChatMessage[];
  aiInput: string;
  setAiInput: (v: string) => void;
  aiLoading: boolean;
  aiStreamStatuses: string[];
  sendAiMessage: () => Promise<void>;
  // AI Auto-Fill (Step 2)
  autoFillReady: boolean;
  autoFillLoading: boolean;
  autoFillSuggestions: AiAutoFillSuggestions | null;
  triggerAutoFill: () => Promise<void>;
  applyAutoFillSuggestions: () => void;
  dismissAutoFillSuggestions: () => void;
  // Members
  allMembers: TeamMember[];
  memberSearch: string;
  setMemberSearch: (v: string) => void;
  teamAutoMembers: string[];
  setTeamAutoMembers: React.Dispatch<React.SetStateAction<string[]>>;
  creatorUserId: string | null;
  // Company data
  companyOffices: string[];
  companyTeams: CompanyTeam[];
  // Sprint 1 (D-R3.3 / BUG-10) — exposed so the dialog can default the
  // language selector to the company-wide default.
  selectedCompany: ReturnType<typeof useAuth>['selectedCompany'];
}

export function useJobPositions(): UseJobPositionsReturn {
  const { selectedCompany, user } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const queryClient = useQueryClient();
  const companyId = selectedCompany?.id;
  // Sprint 2 (S2.C) — pre-create gate. Polls every 60s by default; we only
  // read on click so the slightly stale value is fine.
  const { data: billingStatus, isLoading: billingLoading } = useBillingStatus();
  const showUpgradeWall = useUpgradeWallStore((s) => s.show);

  const positionsQuery = usePositionsQuery(companyId);
  const postingsQuery = usePostingsAllQuery(companyId);
  const companyTeamsQuery = useCompanyTeamsQuery(companyId);

  const positions = positionsQuery.data ?? [];
  const postings = postingsQuery.data ?? [];
  const companyTeams = companyTeamsQuery.data ?? [];
  const loading = !companyId ? false : positionsQuery.isLoading;
  // Sprint 7 (S7.A): publish to the global loading bar.
  useGlobalLoading(loading, 'positions');
  const listPersistKey = companyId ? listViewStorageKey('job-positions', companyId) : undefined;
  const landingAfterReload = isListViewLandingAfterReload(navigationType);
  const savedList = listPersistKey && !landingAfterReload
    ? loadSessionJson<JobPositionsListPersist>(listPersistKey)
    : null;

  const [statusFilter, setStatusFilter] = useState<string>(() => savedList?.statusFilter ?? 'all');
  const [teamFilter, setTeamFilter] = useState<string>(() => savedList?.teamFilter ?? 'all');
  const [search, setSearch] = useState(() => savedList?.search ?? '');
  const [page, setPage] = useState(() => savedList?.page ?? 1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editData, setEditData] = useState<Partial<JobPosition>>(emptyPosition);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Sprint 3 (S3.E) — post-create wizard. After a successful CREATE we don't
  // close the dialog: we transition the same dialog to a confirmation panel
  // ("Position created — activate it now?") with the public landing-page URL
  // so the recruiter can copy/share it or activate the auto-created posting
  // in one click. UPDATE keeps the old behaviour (close + toast).
  const [wizardStep, setWizardStep] = useState<'form' | 'created'>('form');
  const [createdPosition, setCreatedPosition] = useState<{
    id: string;
    title: string;
    landingPageUrl: string;
  } | null>(null);
  const [activatingNow, setActivatingNow] = useState(false);

  const [aiChatOpen, setAiChatOpen] = useState(false);
  const [aiInput, setAiInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiStreamStatuses, setAiStreamStatuses] = useState<string[]>([]);
  const aiAbortRef = useRef<AbortController | null>(null);
  const [autoFillLoading, setAutoFillLoading] = useState(false);

  // Persistent chat session — keyed by "job_position_{positionId}" for existing positions,
  // or "job_position_new" for new positions (cleared on openNewPosition / handleSave).
  // Session auto-resets after AI_CHAT_CONFIG.SESSION_TIMEOUT_HOURS of inactivity.
  const setJobPositionSession = useAiChatStore(s => s.setJobPositionSession);
  const clearJobPositionSession = useAiChatStore(s => s.clearJobPositionSession);

  // Derive session key from editData.id (existing) or 'new'
  const sessionKey = `job_position_${editData.id ?? 'new'}`;

  // Direct reactive selector — re-renders when store hydrates from localStorage on refresh
  const rawJobSession = useAiChatStore(s => s.jobPositionSessions[sessionKey] ?? null);
  const chatSession = rawJobSession && !isSessionExpired(rawJobSession.lastInteractionAt)
    ? rawJobSession
    : EMPTY_JOB_POSITION_SESSION;

  const aiMessages = chatSession.messages;
  const conversationState = chatSession.conversationState;
  const autoFillReady = chatSession.autoFillReady;
  const autoFillSuggestions = chatSession.autoFillSuggestions;

  const [allMembers, setAllMembers] = useState<TeamMember[]>([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [teamAutoMembers, setTeamAutoMembers] = useState<string[]>([]);
  const [creatorUserId, setCreatorUserId] = useState<string | null>(null);

  const companyOffices = selectedCompany?.localization?.length
    ? selectedCompany.localization.map(l => l.address)
    : [];

  const setPositions = useCallback((
    updater: JobPosition[] | ((prev: JobPosition[]) => JobPosition[]),
  ) => {
    if (!companyId) return;
    setPositionsCache(queryClient, companyId, updater);
  }, [companyId, queryClient]);

  useEffect(() => {
    if (selectedCompany) {
      void api.team.list(selectedCompany.id).then(setAllMembers);
    }
  }, [selectedCompany]);

  // Sprint 2 (S2.E) — refresh on plan change. Same reasoning as in
  // `useApplicationsPageData`: the positions cap may have just expanded
  // (or collapsed on downgrade), so the pre-create gate in
  // `openNewPosition` needs the latest count to make the right decision.
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;
    void import('@/lib/billingEvents').then(({ onPlanChanged }) => {
      if (cancelled) return;
      unsubscribe = onPlanChanged(() => {
        void queryClient.invalidateQueries({ queryKey: queryKeys.positions(companyId) });
        void queryClient.invalidateQueries({ queryKey: queryKeys.postingsAll(companyId) });
      });
    });
    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [companyId, queryClient]);

  const postingsCountMap = postings.reduce<Record<string, number>>((acc, p) => {
    acc[p.positionId] = (acc[p.positionId] || 0) + 1;
    return acc;
  }, {});

  useEffect(() => {
    const state = location.state as any;
    if (state?.editPosition) {
      setEditData(state.editPosition);
      setDialogOpen(true);
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  const filtered = useMemo(() => positions.filter(p => {
    if (statusFilter !== 'all' && p.status !== statusFilter) return false;
    if (teamFilter === 'unassigned') {
      if (p.team) return false;
    } else if (teamFilter !== 'all' && p.team !== teamFilter) return false;
    if (search && !p.title.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [positions, statusFilter, teamFilter, search]);

  const totalFiltered = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / POSITIONS_PAGE_SIZE));
  const safePage = Math.min(page, totalPages);

  const paginatedFiltered = useMemo(() => {
    const start = (safePage - 1) * POSITIONS_PAGE_SIZE;
    return filtered.slice(start, start + POSITIONS_PAGE_SIZE);
  }, [filtered, safePage]);

  useSkipInitialEffect(() => {
    setPage(1);
  }, [statusFilter, teamFilter, search]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const handleSave = useCallback(async () => {
    if (!editData.title || !editData.description) {
      toast.error('Title and description are required');
      return;
    }
    if (!selectedCompany) {
      toast.error('No company selected');
      return;
    }
    const cleanData = { ...editData };
    if (cleanData.seniority === '__other__') cleanData.seniority = undefined;
    if (cleanData.contractType === '__other__') cleanData.contractType = undefined;
    cleanData.location = normalizeJobLocation(cleanData.location);
    if (!cleanData.id && (!cleanData.assignedMembers || cleanData.assignedMembers.length < 1)) {
      toast.error(t('positions.atLeastOneMember'));
      return;
    }
    setSaving(true);
    try {
      if (cleanData.id) {
        const prevRow = positions.find(p => p.id === cleanData.id);
        const payload: typeof cleanData = { ...cleanData };
        const members = cleanData.assignedMembers ?? prevRow?.assignedMembers;
        if (members !== undefined) {
          payload.assignedMembers = members;
        } else {
          delete payload.assignedMembers;
        }
        const saved = await api.positions.update(selectedCompany.id, cleanData.id, payload);
        setPositions(prev => prev.map(p => p.id === saved.id ? saved : p));
        toast.success('Position updated');
        // UPDATE keeps the old behaviour: close dialog, no wizard.
        setDialogOpen(false);
        setEditData(emptyPosition);
        setAiChatOpen(false);
        clearJobPositionSession(sessionKey);
        setAutoFillLoading(false);
      } else {
        // Sprint 3 (S3.E) — CREATE: keep the dialog open and switch it to
        // the post-create confirmation panel so the recruiter can decide
        // whether to activate the position immediately and grab the public
        // landing-page URL. Both branches share the same dialog frame so
        // there's no jarring close/reopen.
        const { position: saved, postingUrl } = await api.positions.create(selectedCompany.id, cleanData);
        setPositions(prev => [...prev, saved]);
        clearJobPositionSession(sessionKey);
        setAutoFillLoading(false);
        setAiChatOpen(false);
        setCreatedPosition({
          id: saved.id,
          title: saved.title,
          landingPageUrl: postingUrl ?? '',
        });
        setWizardStep('created');
      }
    } catch (err: any) {
      // Sprint 2 (S2.C) — 402 backstop. `httpClient.ts` already dispatched
      // the `hiromatch:upgrade-wall` event (the modal is now open). Close
      // the position dialog underneath so the user isn't left staring at a
      // form they can't submit. Reset the draft so reopening starts clean.
      if (err?.status === 402) {
        setDialogOpen(false);
        setEditData(emptyPosition);
        setAiChatOpen(false);
        clearJobPositionSession(sessionKey);
        return;
      }
      toast.error(err?.message || 'Failed to save position. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [editData, selectedCompany, positions, t, sessionKey, clearJobPositionSession, setPositions]);

  // Sprint 3 (S3.E) — wizard step-2 handlers. Both close the dialog cleanly
  // and reset the form so the next "New position" click starts from scratch.
  const closeWizardAndReset = useCallback(() => {
    setDialogOpen(false);
    setWizardStep('form');
    setCreatedPosition(null);
    setEditData(emptyPosition);
  }, []);

  /**
   * handleSaveAsDraft — recruiter chose "Save as draft" in the wizard. The
   * position was already persisted on save, so we only navigate to its
   * detail page (so the recruiter can keep editing). The public link is
   * inactive until the position is activated.
   */
  const handleSaveAsDraft = useCallback(() => {
    if (!createdPosition) {
      closeWizardAndReset();
      return;
    }
    const id = createdPosition.id;
    closeWizardAndReset();
    navigate(`/job-positions/${id}`);
  }, [createdPosition, closeWizardAndReset, navigate]);

  /**
   * handleActivateAndOpen — recruiter chose "Activate now". Flips the
   * position to ACTIVE (which side-effects the auto-created posting to
   * `isActive: true` on the BE) and opens the public landing URL in a new
   * tab so the recruiter can verify it's live + share it. We also navigate
   * the current tab to the position detail. Errors fall back to the wizard
   * with a toast — the position stays as draft, no orphaned state.
   */
  const handleActivateAndOpen = useCallback(async () => {
    if (!selectedCompany || !createdPosition) return;
    setActivatingNow(true);
    // Sprint 7+ follow-up (20/05/26): the previous flow called window.open
    // AFTER `await api.positions.updateStatus(...)`. Two real-world bugs:
    //
    //  1) Popup blocker — modern browsers (Chrome/Firefox/Safari) only
    //     allow window.open without prompt when invoked synchronously
    //     during a user-initiated event handler. After the await, that
    //     gesture token is gone, so opening the public page either fails
    //     silently or surfaces a popup-blocked indicator.
    //
    //  2) "Pagina non trovata" — even when the popup did open, the new
    //     tab fired the public-posting fetch before Firestore had fully
    //     propagated the `posting.isActive: true` write. The endpoint
    //     guards on `!posting.isActive → 404`, so the recruiter saw a
    //     not-found screen even though activation succeeded a few ms
    //     later.
    //
    // Fix: open a blank tab IMMEDIATELY (still inside the user gesture),
    // then await the activation, and only AFTER the API returns 200 we
    // point the tab at the landing URL. If the activation fails we close
    // the blank tab so we don't leave a stray about:blank window around.
    // We also drop `noopener` here intentionally — without it the browser
    // returns null and we lose the handle.
    const target: Window | null = createdPosition.landingPageUrl
      ? window.open('about:blank', '_blank')
      : null;
    try {
      await api.positions.updateStatus(selectedCompany.id, createdPosition.id, 'active');
      setPositions(prev => prev.map(p => p.id === createdPosition.id ? { ...p, status: 'active' } : p));
      if (target && !target.closed && createdPosition.landingPageUrl) {
        try { target.opener = null; } catch { /* ignore — some browsers freeze opener */ }
        target.location.replace(createdPosition.landingPageUrl);
      } else if (createdPosition.landingPageUrl) {
        // Popup was blocked — fall back to same-tab nav after the
        // backoffice navigation so the recruiter still ends up somewhere
        // useful. The detail page exposes a "View public page" link they
        // can click manually.
        toast.info(t('positions.activatedPopupBlocked'));
      }
      const id = createdPosition.id;
      closeWizardAndReset();
      navigate(`/job-positions/${id}`);
      toast.success(t('positions.activated'));
    } catch (err: any) {
      if (target && !target.closed) target.close();
      // 402 → upgrade modal already opens via httpClient. Stay on the
      // wizard so the recruiter can still grab the link or save as draft.
      if (err?.status === 402) {
        setActivatingNow(false);
        return;
      }
      toast.error(err?.message || 'Failed to activate position. Please try again.');
    } finally {
      setActivatingNow(false);
    }
  }, [selectedCompany, createdPosition, closeWizardAndReset, navigate, t]);

  /** Copy the public link to clipboard with a small toast confirmation. */
  const copyCreatedPositionLink = useCallback(async () => {
    if (!createdPosition?.landingPageUrl) return;
    try {
      await navigator.clipboard.writeText(createdPosition.landingPageUrl);
      toast.success(t('positions.linkCopied'));
    } catch {
      toast.error('Could not copy to clipboard');
    }
  }, [createdPosition, t]);

  const handleDelete = useCallback(async (id: string) => {
    if (!selectedCompany) return;
    try {
      await api.positions.delete(selectedCompany.id, id);
      // Soft-delete: backend sets status to 'cancelled' — keep in list with updated status
      setPositions(prev => prev.map(p => p.id === id ? { ...p, status: 'cancelled' } : p));
      toast.success(t('positions.positionCancelled'));
    } catch (err: any) {
      toast.error(err?.message || 'Failed to cancel position');
    }
    setDeleteId(null);
  }, [selectedCompany, t]);

  const handlePermanentDelete = useCallback(async (id: string) => {
    if (!selectedCompany) return;
    try {
      await api.positions.permanentDelete(selectedCompany.id, id);
      // Hard-delete: remove from local list entirely
      setPositions(prev => prev.filter(p => p.id !== id));
      toast.success(t('positions.positionDeleted'));
    } catch (err: any) {
      toast.error(err?.message || 'Failed to permanently delete position');
    }
    setDeleteId(null);
  }, [selectedCompany, t]);

  const handleStatusToggle = useCallback(async (pos: JobPosition) => {
    if (!selectedCompany) return;
    const newStatus = pos.status === 'active' ? 'closed' : 'active';
    try {
      await api.positions.updateStatus(selectedCompany.id, pos.id, newStatus);
      setPositions(prev => prev.map(p => p.id === pos.id ? { ...p, status: newStatus as any } : p));
      toast.success(t('positions.positionUpdated'));
    } catch {
      toast.error('Failed to update status');
    }
  }, [selectedCompany, t]);

  const openNewPosition = useCallback(() => {
    // Sprint 2 (S2.C) — pre-create cap gate. The BE blocks `POST /positions`
    // with 402 when `active_positions` ≥ `limits.positions`, but doing the
    // round-trip *after* the recruiter has filled the entire form is hostile
    // UX. We mirror the same gate locally so the upgrade wall opens before
    // the dialog is even shown.
    //
    // Counter source: count of positions in the local list with status
    // 'active'. We deliberately *don't* include 'closed' / 'cancelled' /
    // 'draft' to mirror the BE's `counters.active_positions` increment
    // (driven by `JobPositionService.changeStatus` on activation).
    //
    // Backstop: when the local cache is stale (e.g. another tab just
    // created a position), the BE 402 still fires and `httpClient.ts`
    // dispatches `hiromatch:upgrade-wall` — we also close the dialog in
    // `handleSave` so we don't leave a useless form mounted underneath.
    //
    // Fix (22/05/26): if billingStatus is still loading on first click,
    // don't open the dialog — the user should wait. Previously we allowed
    // opening which led to inconsistent UX (first click opens, second blocks).
    if (billingLoading && !billingStatus) {
      // Still loading billing status — don't open yet, user should wait
      return;
    }
    if (billingStatus && !billingStatus.isUnlimited) {
      const limit = billingStatus.limits?.positions ?? 0;
      const activeCount = positions.filter((p) => p.status === 'active').length;
      // Treat `limit === 0` as "this plan has no positions allowance" (e.g.
      // some legacy / edge plans) — same outcome: cap reached, upsell.
      if (limit > 0 && activeCount >= limit) {
        const planId = billingStatus.planId ?? 'freemium_trial';
        showUpgradeWall({
          reason: 'structural_limit',
          pool_type: 'structural',
          required_plan: nextTierForCap(planId) ?? undefined,
          upgrade_url: `/profile?tab=billing&plan=${nextTierForCap(planId) ?? 'sme_starter'}`,
        });
        return;
      }
    }

    const creatorMember = user ? allMembers.find(m => m.userId === user.id || m.email === user.email) : null;
    const creatorId = creatorMember?.userId || null;
    const defaultMembers = creatorId ? [creatorId] : [];
    setCreatorUserId(creatorId);
    setEditData({ ...emptyPosition, assignedMembers: defaultMembers });
    setMemberSearch('');
    setTeamAutoMembers([]);
    // Clear the 'new' session slot — prevents leaking context from a previous new-position dialog
    // (ADR Section 9.1). Existing-position sessions are preserved for persistence.
    clearJobPositionSession('job_position_new');
    setAiInput('');
    setAiChatOpen(false);
    setAutoFillLoading(false);
    // Sprint 3 (S3.E): always start from the form step so a previous
    // "created" panel doesn't leak across dialog opens.
    setWizardStep('form');
    setCreatedPosition(null);
    setDialogOpen(true);
  }, [user, allMembers, clearJobPositionSession, billingStatus, billingLoading, positions, showUpgradeWall]);

  // Auto-open create dialog when navigated with ?action=new
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('action') === 'new' && !dialogOpen) {
      openNewPosition();
      // Clean the URL without re-rendering
      window.history.replaceState({}, '', location.pathname);
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendAiMessage = useCallback(async () => {
    if (!aiInput.trim() || !selectedCompany) return;

    aiAbortRef.current?.abort();
    aiAbortRef.current = new AbortController();

    const userMessage = aiInput.trim();
    const now = new Date().toISOString();

    // Delta tracking: only NEW form fields (not previously sent) are included
    const knownFields = conversationState?.knownFormFields ?? [];
    const formContextDelta = buildFormContextDelta(editData, knownFields);
    const newKnownFields = [...knownFields, ...Object.keys(formContextDelta)];

    // Append user message + AI placeholder
    setJobPositionSession(sessionKey, {
      messages: [...aiMessages, { role: 'user', text: userMessage }, { role: 'ai', text: '' }],
      lastInteractionAt: now,
    });
    setAiInput('');
    setAiLoading(true);
    setAiStreamStatuses([]);

    try {
      await api.ai.chatStream(
        selectedCompany.id,
        {
          profileName: 'job_position_assistant',
          message: userMessage,
          conversationState,
          context: Object.keys(formContextDelta).length > 0
            ? { formContext: formContextDelta }
            : undefined,
        },
        {
          onFinal: (response) => {
            const aiNow = new Date().toISOString();
            setJobPositionSession(sessionKey, {
              messages: [...aiMessages, { role: 'user', text: userMessage }, { role: 'ai', text: response.text }],
              conversationState: {
                ...response.conversationState,
                knownFormFields: newKnownFields,
              },
              autoFillReady: autoFillReady || !!response.artifacts?.autoFillReady,
              lastInteractionAt: aiNow,
            });
            setAiStreamStatuses([]);
            setAiLoading(false);
            emitBillingUsageChanged();

            // Log statusMessages for debugging (not shown in chat UI)
            if (response.statusMessages.length > 0) {
              console.debug('[AI Assistant] status:', response.statusMessages);
            }
          },
          onError: (_errMsg) => {
            setJobPositionSession(sessionKey, {
              messages: [...aiMessages, { role: 'user', text: userMessage }, { role: 'ai', text: t('aiChat.generic_error') }],
              lastInteractionAt: new Date().toISOString(),
            });
            setAiStreamStatuses([]);
            setAiLoading(false);
          },
        },
        aiAbortRef.current.signal,
      );
    } catch (err: any) {
      // Sprint 2 (S2.F) — distinguish 402 reasons so the in-chat AI bubble
      // tells the recruiter exactly what's blocking them. The shared
      // `hiromatch:upgrade-wall` modal is also opening in parallel.
      const billingReason = (err?.data as { reason?: string } | undefined)?.reason;
      const errorText = err?.status === 402 && billingReason === 'plan_required'
        ? t('aiChat.plan_required')
        : err?.status === 402 && billingReason === 'insufficient_balance'
        ? t('aiChat.balance_exhausted')
        : err?.status === 402
        ? t('billing.aiNotEnabled')
        : err?.status === 400
        ? 'Invalid request. Please try again.'
        : err?.message?.includes('demo')
        ? t('aiChat.demo_unavailable')
        : t('aiChat.generic_error');
      setJobPositionSession(sessionKey, {
        messages: [...aiMessages, { role: 'user', text: userMessage }, { role: 'ai', text: errorText }],
        lastInteractionAt: new Date().toISOString(),
      });
      setAiStreamStatuses([]);
      setAiLoading(false);
    }
  }, [aiInput, selectedCompany, editData, conversationState, sessionKey, aiMessages, autoFillReady, setJobPositionSession, t]);

  // ---------------------------------------------------------------------------
  // AI Auto-Fill callbacks (Step 2)
  // ---------------------------------------------------------------------------

  /**
   * triggerAutoFill — calls POST /ai/autofill with the current conversationState
   * and stores the structured suggestions for the recruiter to review.
   */
  const triggerAutoFill = useCallback(async () => {
    if (!conversationState || !selectedCompany) return;
    setAutoFillLoading(true);
    try {
      const response = await api.ai.autofill(selectedCompany.id, {
        profileName: 'job_position_autofill',
        conversationState,
      });
      setJobPositionSession(sessionKey, { autoFillSuggestions: response.suggestions });
      emitBillingUsageChanged();
    } catch (err: any) {
      const errorText = err?.status === 402
        ? t('billing.aiNotEnabled')
        : err?.message?.includes('demo')
        ? 'AI features are not available in demo mode.'
        : 'Auto-fill failed. Please try again.';
      toast.error(errorText);
    } finally {
      setAutoFillLoading(false);
    }
  }, [selectedCompany, conversationState, sessionKey, setJobPositionSession]);

  /**
   * applyAutoFillSuggestions — applies the AI suggestions to the form.
   * Only applies fields that are present in the suggestions (no undefined overwrite).
   * Closes the suggestions preview after applying.
   */
  const applyAutoFillSuggestions = useCallback(() => {
    if (!autoFillSuggestions) return;
    setEditData(prev => ({
      ...prev,
      ...(autoFillSuggestions.title       ? { title: autoFillSuggestions.title } : {}),
      ...(autoFillSuggestions.description ? { description: autoFillSuggestions.description } : {}),
      ...(autoFillSuggestions.seniority   ? { seniority: autoFillSuggestions.seniority } : {}),
      ...(autoFillSuggestions.contractType ? { contractType: autoFillSuggestions.contractType } : {}),
      ...(autoFillSuggestions.workMode    ? { workMode: autoFillSuggestions.workMode as any } : {}),
      ...(normalizeJobLocation(autoFillSuggestions.location)
        ? { location: normalizeJobLocation(autoFillSuggestions.location) }
        : {}),
      ...(autoFillSuggestions.salaryMin   != null ? { salaryMin: autoFillSuggestions.salaryMin } : {}),
      ...(autoFillSuggestions.salaryMax   != null ? { salaryMax: autoFillSuggestions.salaryMax } : {}),
      ...(autoFillSuggestions.salaryCurrency ? { salaryCurrency: autoFillSuggestions.salaryCurrency } : {}),
      ...(autoFillSuggestions.budget      != null ? { budget: autoFillSuggestions.budget } : {}),
      ...(autoFillSuggestions.budgetCurrency ? { budgetCurrency: autoFillSuggestions.budgetCurrency } : {}),
      ...(autoFillSuggestions.notifyCandidate != null ? { notifyCandidate: autoFillSuggestions.notifyCandidate } : {}),
    }));
    setJobPositionSession(sessionKey, { autoFillSuggestions: null }); // close preview after apply
  }, [autoFillSuggestions, sessionKey, setJobPositionSession]);

  /**
   * dismissAutoFillSuggestions — discards the suggestions without applying.
   */
  const dismissAutoFillSuggestions = useCallback(() => {
    setJobPositionSession(sessionKey, { autoFillSuggestions: null });
  }, [sessionKey, setJobPositionSession]);

  return {
    positions,
    loading,
    setPositions,
    postingsCountMap,
    filtered,
    paginatedFiltered,
    page: safePage,
    totalPages,
    totalFiltered,
    setPage,
    statusFilter,
    setStatusFilter,
    teamFilter,
    setTeamFilter,
    search,
    setSearch,
    dialogOpen,
    setDialogOpen,
    editData,
    setEditData,
    deleteId,
    setDeleteId,
    saving,
    handleSave,
    handleDelete,
    handlePermanentDelete,
    handleStatusToggle,
    openNewPosition,
    /** True while billing status is being fetched — used to disable "New Position" button */
    billingLoading,
    // Sprint 3 (S3.E)
    wizardStep,
    createdPosition,
    activatingNow,
    handleSaveAsDraft,
    handleActivateAndOpen,
    copyCreatedPositionLink,
    closeWizardAndReset,
    aiChatOpen,
    setAiChatOpen,
    aiMessages,
    aiInput,
    setAiInput,
    aiLoading,
    aiStreamStatuses,
    sendAiMessage,
    autoFillReady,
    autoFillLoading,
    autoFillSuggestions,
    triggerAutoFill,
    applyAutoFillSuggestions,
    dismissAutoFillSuggestions,
    allMembers,
    memberSearch,
    setMemberSearch,
    teamAutoMembers,
    setTeamAutoMembers,
    creatorUserId,
    companyOffices,
    companyTeams,
    // Sprint 1 (D-R3.3 / BUG-10) — exposed so the dialog can default the
    // language selector to the company-wide default.
    selectedCompany,
  };
}
